import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { restaurantService } from '../services/restaurants';
import { Restaurant } from '../types';

// Claves para localStorage
const ACCESS_RESTAURANTS_KEY = 'ots_access_restaurants';
const ACCESS_RESTAURANTS_TIMESTAMP_KEY = 'ots_access_restaurants_timestamp';
const USER_RESTAURANTS_KEY = 'ots_user_restaurants';
const USER_RESTAURANTS_TIMESTAMP_KEY = 'ots_user_restaurants_timestamp';
const CACHE_EXPIRY = 30 * 60 * 1000; // 30 minutos

interface RestaurantCacheContextProps {
  // Cache para restaurantes públicos (solo para clientes)
  publicRestaurants: Restaurant[];
  loadingPublicRestaurants: boolean;
  errorPublicRestaurants: string | null;
  loadPublicRestaurants: () => Promise<Restaurant[]>;
  
  // Cache para restaurantes del usuario (solo para admin/owners)
  userRestaurants: Restaurant[];
  loadingUserRestaurants: boolean;
  errorUserRestaurants: string | null;
  loadUserRestaurants: (userId: string, userEmail?: string) => Promise<Restaurant[]>;
  
  // Cache para restaurantes con acceso (solo para waiters/managers)
  accessRestaurants: Restaurant[];
  loadingAccessRestaurants: boolean;
  errorAccessRestaurants: string | null;
  loadAccessRestaurants: (userId: string, userEmail?: string) => Promise<Restaurant[]>;
  
  // Función para verificar si el usuario tiene restaurantes (sin cargar datos completos)
  checkUserHasRestaurants: (userId: string, userEmail?: string) => Promise<boolean>;
  
  // Función para limpiar cache
  clearCache: () => void;
  
  // Función para invalidar cache específico
  invalidateCache: (type: 'public' | 'user' | 'access') => void;
  
  // Función para cargar restaurantes con acceso desde localStorage
  loadAccessRestaurantsFromStorage: () => Restaurant[];
  
  // Función para guardar restaurantes con acceso en localStorage
  saveAccessRestaurantsToStorage: (restaurants: Restaurant[]) => void;
  
  // Función para cargar restaurantes del usuario desde localStorage
  loadUserRestaurantsFromStorage: (userId: string) => Restaurant[];
  
  // Función para guardar restaurantes del usuario en localStorage
  saveUserRestaurantsToStorage: (userId: string, restaurants: Restaurant[]) => void;
  
  // Función para refrescar restaurantes
  refreshRestaurants: (userId: string, userEmail?: string) => Promise<void>;
}

const RestaurantCacheContext = createContext<RestaurantCacheContextProps | undefined>(undefined);

export const RestaurantCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Estados para restaurantes públicos
  const [publicRestaurants, setPublicRestaurants] = useState<Restaurant[]>([]);
  const [loadingPublicRestaurants, setLoadingPublicRestaurants] = useState(false);
  const [errorPublicRestaurants, setErrorPublicRestaurants] = useState<string | null>(null);
  const [publicRestaurantsLoaded, setPublicRestaurantsLoaded] = useState(false);
  
  // Estados para restaurantes del usuario
  const [userRestaurants, setUserRestaurants] = useState<Restaurant[]>([]);
  const [loadingUserRestaurants, setLoadingUserRestaurants] = useState(false);
  const [errorUserRestaurants, setErrorUserRestaurants] = useState<string | null>(null);
  const userRestaurantsCache = useRef<Map<string, Restaurant[]>>(new Map());
  
  // Estados para restaurantes con acceso
  const [accessRestaurants, setAccessRestaurants] = useState<Restaurant[]>([]);
  const [loadingAccessRestaurants, setLoadingAccessRestaurants] = useState(false);
  const [errorAccessRestaurants, setErrorAccessRestaurants] = useState<string | null>(null);
  const accessRestaurantsCache = useRef<Map<string, Restaurant[]>>(new Map());
  
  // Referencias para evitar peticiones simultáneas
  const publicLoadingPromise = useRef<Promise<Restaurant[]> | null>(null);
  const userLoadingPromises = useRef<Map<string, Promise<Restaurant[]>>>(new Map());
  const accessLoadingPromises = useRef<Map<string, Promise<Restaurant[]>>>(new Map());
  const checkRestaurantsPromises = useRef<Map<string, Promise<boolean>>>(new Map());
  
  // Cargar restaurantes con acceso desde localStorage al inicializar
  useEffect(() => {
    const storedRestaurants = loadAccessRestaurantsFromStorage();
    if (storedRestaurants.length > 0) {
      console.log('📋 RestaurantCache - Inicializando con restaurantes del localStorage:', storedRestaurants.length);
      setAccessRestaurants(storedRestaurants);
    }
  }, []); // Solo se ejecuta una vez al montar el componente

  // Función para invalidar cache específico (declarada primero)
  const invalidateCache = useCallback((type: 'public' | 'user' | 'access') => {
    console.log('🔄 RestaurantCache - Invalidando cache:', type);
    
    switch (type) {
      case 'public':
        setPublicRestaurants([]);
        setPublicRestaurantsLoaded(false);
        publicLoadingPromise.current = null;
        break;
      case 'user':
        setUserRestaurants([]);
        userRestaurantsCache.current.clear();
        userLoadingPromises.current.clear();
        break;
      case 'access':
        setAccessRestaurants([]);
        accessRestaurantsCache.current.clear();
        accessLoadingPromises.current.clear();
        // También limpiar localStorage
        localStorage.removeItem(ACCESS_RESTAURANTS_KEY);
        localStorage.removeItem(ACCESS_RESTAURANTS_TIMESTAMP_KEY);
        break;
    }
  }, []);

  // Función para cargar restaurantes con acceso desde localStorage
  const loadAccessRestaurantsFromStorage = useCallback(() => {
    const cached = localStorage.getItem(ACCESS_RESTAURANTS_KEY);
    const timestamp = localStorage.getItem(ACCESS_RESTAURANTS_TIMESTAMP_KEY);

    if (cached && timestamp) {
      const cachedData = JSON.parse(cached);
      const cachedTimestamp = parseInt(timestamp, 10);

      if (Date.now() - cachedTimestamp < CACHE_EXPIRY) {
        setAccessRestaurants(cachedData);
        console.log('📋 RestaurantCache - Cargando restaurantes con acceso del localStorage:', cachedData.length);
        return cachedData;
      } else {
        console.log('🧹 RestaurantCache - Cache de restaurantes con acceso expirado, cargando desde API.');
        invalidateCache('access'); // Invalida el cache para forzar la recarga
      }
    }
    return [];
  }, [invalidateCache]);

  // Función para guardar restaurantes con acceso en localStorage
  const saveAccessRestaurantsToStorage = useCallback((restaurants: Restaurant[]) => {
    const now = Date.now();
    localStorage.setItem(ACCESS_RESTAURANTS_KEY, JSON.stringify(restaurants));
    localStorage.setItem(ACCESS_RESTAURANTS_TIMESTAMP_KEY, now.toString());
    console.log('✅ RestaurantCache - Guardando restaurantes con acceso en localStorage:', restaurants.length);
  }, []);

  // Función para cargar restaurantes del usuario desde localStorage
  const loadUserRestaurantsFromStorage = useCallback((userId: string) => {
    const cached = localStorage.getItem(`${USER_RESTAURANTS_KEY}_${userId}`);
    const timestamp = localStorage.getItem(`${USER_RESTAURANTS_TIMESTAMP_KEY}_${userId}`);

    if (cached && timestamp) {
      const cachedData = JSON.parse(cached);
      const cachedTimestamp = parseInt(timestamp, 10);

      if (Date.now() - cachedTimestamp < CACHE_EXPIRY) {
        setUserRestaurants(cachedData);
        console.log('📋 RestaurantCache - Cargando restaurantes del usuario del localStorage:', cachedData.length);
        return cachedData;
      } else {
        console.log('🧹 RestaurantCache - Cache de restaurantes del usuario expirado, cargando desde API.');
        // Limpiar localStorage expirado
        localStorage.removeItem(`${USER_RESTAURANTS_KEY}_${userId}`);
        localStorage.removeItem(`${USER_RESTAURANTS_TIMESTAMP_KEY}_${userId}`);
      }
    }
    return [];
  }, []);

  // Función para guardar restaurantes del usuario en localStorage
  const saveUserRestaurantsToStorage = useCallback((userId: string, restaurants: Restaurant[]) => {
    const now = Date.now();
    localStorage.setItem(`${USER_RESTAURANTS_KEY}_${userId}`, JSON.stringify(restaurants));
    localStorage.setItem(`${USER_RESTAURANTS_TIMESTAMP_KEY}_${userId}`, now.toString());
    console.log('✅ RestaurantCache - Guardando restaurantes del usuario en localStorage:', restaurants.length);
  }, []);

  // Cargar restaurantes públicos (solo una vez)
  const loadPublicRestaurants = useCallback(async (): Promise<Restaurant[]> => {
    // Si ya están cargados (aunque la lista esté vacía), retornar inmediatamente
    // Evita reintentos infinitos cuando la API devuelve 0 restaurantes
    if (publicRestaurantsLoaded) {
      console.log('📋 RestaurantCache - Usando restaurantes públicos del cache:', publicRestaurants.length);
      return publicRestaurants;
    }
    
    // Si ya hay una petición en progreso, esperar
    if (publicLoadingPromise.current) {
      console.log('⏳ RestaurantCache - Esperando petición de restaurantes públicos en progreso...');
      return await publicLoadingPromise.current;
    }
    
    // Iniciar nueva petición
    setLoadingPublicRestaurants(true);
    setErrorPublicRestaurants(null);
    
    publicLoadingPromise.current = (async () => {
      try {
        console.log('🔄 RestaurantCache - Cargando restaurantes públicos...');
        const restaurants = await restaurantService.getRestaurants();
        
        setPublicRestaurants(restaurants);
        setPublicRestaurantsLoaded(true);
        console.log('✅ RestaurantCache - Restaurantes públicos cargados:', restaurants.length);
        
        return restaurants;
      } catch (error) {
        const errorMessage = 'Error al cargar restaurantes públicos';
        console.error('❌ RestaurantCache -', errorMessage, error);
        setErrorPublicRestaurants(errorMessage);
        return [];
      } finally {
        setLoadingPublicRestaurants(false);
        publicLoadingPromise.current = null;
      }
    })();
    
    return await publicLoadingPromise.current;
  }, [publicRestaurants, publicRestaurantsLoaded]);

  // Cargar restaurantes del usuario (con cache por userId y localStorage)
  const loadUserRestaurants = useCallback(async (userId: string, userEmail?: string): Promise<Restaurant[]> => {
    // Limpiar localStorage para forzar datos frescos
    localStorage.removeItem(`${USER_RESTAURANTS_KEY}_${userId}`);
    localStorage.removeItem(`${USER_RESTAURANTS_TIMESTAMP_KEY}_${userId}`);
    
    // Verificar cache en memoria
    if (userRestaurantsCache.current.has(userId)) {
      const cached = userRestaurantsCache.current.get(userId)!;
      console.log('📋 RestaurantCache - Usando restaurantes del usuario del cache en memoria:', cached.length);
      setUserRestaurants(cached);
      return cached;
    }
    
    // Si ya hay una petición en progreso para este usuario, esperar
    if (userLoadingPromises.current.has(userId)) {
      console.log('⏳ RestaurantCache - Esperando petición de restaurantes del usuario en progreso...');
      return await userLoadingPromises.current.get(userId)!;
    }
    
    // Iniciar nueva petición
    setLoadingUserRestaurants(true);
    setErrorUserRestaurants(null);
    
    const promise = (async () => {
      try {
        console.log('🔄 RestaurantCache - Cargando restaurantes del usuario desde API:', userId);
        // Usar el endpoint que devuelve todos los restaurantes con permisos (no solo owner)
        const restaurants = await restaurantService.getRestaurantsWithAccess(userId, userEmail) as Restaurant[];
        
        // Guardar en cache en memoria
        userRestaurantsCache.current.set(userId, restaurants);
        setUserRestaurants(restaurants);
        
        // Guardar en localStorage para persistencia
        saveUserRestaurantsToStorage(userId, restaurants);
        
        console.log('✅ RestaurantCache - Restaurantes del usuario cargados y guardados:', restaurants.length);
        
        return restaurants;
      } catch (error) {
        const errorMessage = 'Error al cargar restaurantes del usuario';
        console.error('❌ RestaurantCache -', errorMessage, error);
        setErrorUserRestaurants(errorMessage);
        return [];
      } finally {
        setLoadingUserRestaurants(false);
        userLoadingPromises.current.delete(userId);
      }
    })();
    
    userLoadingPromises.current.set(userId, promise);
    return await promise;
  }, [saveUserRestaurantsToStorage]);

  // Cargar restaurantes con acceso (con cache por userId-email y localStorage)
  const loadAccessRestaurants = useCallback(async (userId: string, userEmail?: string): Promise<Restaurant[]> => {
    const cacheKey = `${userId}-${userEmail || 'no-email'}`;
    
    // Primero intentar cargar desde localStorage
    const storedRestaurants = loadAccessRestaurantsFromStorage();
    if (storedRestaurants.length > 0) {
      console.log('📋 RestaurantCache - Usando restaurantes con acceso del localStorage:', storedRestaurants.length);
      setAccessRestaurants(storedRestaurants);
      return storedRestaurants;
    }
    
    // Verificar cache en memoria
    if (accessRestaurantsCache.current.has(cacheKey)) {
      const cached = accessRestaurantsCache.current.get(cacheKey)!;
      console.log('📋 RestaurantCache - Usando restaurantes con acceso del cache en memoria:', cached.length);
      setAccessRestaurants(cached);
      return cached;
    }
    
    // Si ya hay una petición en progreso para esta clave, esperar
    if (accessLoadingPromises.current.has(cacheKey)) {
      console.log('⏳ RestaurantCache - Esperando petición de restaurantes con acceso en progreso...');
      return await accessLoadingPromises.current.get(cacheKey)!;
    }
    
    // Iniciar nueva petición
    setLoadingAccessRestaurants(true);
    setErrorAccessRestaurants(null);
    
    const promise = (async () => {
      try {
        console.log('🔄 RestaurantCache - Cargando restaurantes con acceso desde API:', { userId, userEmail });
        const restaurants = await restaurantService.getRestaurantsWithAccess(userId, userEmail);
        
        // Guardar en cache en memoria
        accessRestaurantsCache.current.set(cacheKey, restaurants);
        setAccessRestaurants(restaurants);
        
        // Guardar en localStorage para persistencia
        saveAccessRestaurantsToStorage(restaurants);
        
        console.log('✅ RestaurantCache - Restaurantes con acceso cargados y guardados:', restaurants.length);
        
        return restaurants;
      } catch (error) {
        const errorMessage = 'Error al cargar restaurantes con acceso';
        console.error('❌ RestaurantCache -', errorMessage, error);
        setErrorAccessRestaurants(errorMessage);
        return [];
      } finally {
        setLoadingAccessRestaurants(false);
        accessLoadingPromises.current.delete(cacheKey);
      }
    })();
    
    accessLoadingPromises.current.set(cacheKey, promise);
    return await promise;
  }, [loadAccessRestaurantsFromStorage, saveAccessRestaurantsToStorage]);

  // Verificar si el usuario tiene restaurantes (sin cargar datos completos)
  const checkUserHasRestaurants = useCallback(async (userId: string, userEmail?: string): Promise<boolean> => {
    const cacheKey = `${userId}-${userEmail || 'no-email'}-check`;
    
    // Si ya hay una petición en progreso para esta verificación, esperar
    if (checkRestaurantsPromises.current.has(cacheKey)) {
      console.log('⏳ RestaurantCache - Esperando verificación de restaurantes en progreso...');
      return await checkRestaurantsPromises.current.get(cacheKey)!;
    }
    
    const promise = (async () => {
      try {
        console.log('🔍 RestaurantCache - Verificando si el usuario tiene restaurantes:', { userId, userEmail });
        
        // Hacer una petición ligera solo para verificar si hay restaurantes
        const restaurants = await restaurantService.getRestaurantsWithAccess(userId, userEmail);
        const hasRestaurants = restaurants.length > 0;
        
        console.log('✅ RestaurantCache - Usuario tiene restaurantes:', hasRestaurants);
        return hasRestaurants;
      } catch (error) {
        console.error('❌ RestaurantCache - Error verificando restaurantes:', error);
        return false;
      } finally {
        checkRestaurantsPromises.current.delete(cacheKey);
      }
    })();
    
    checkRestaurantsPromises.current.set(cacheKey, promise);
    return await promise;
  }, []);

  // Función para refrescar restaurantes (usar cuando se crea uno nuevo)
  const refreshRestaurants = useCallback(async (userId: string, userEmail?: string) => {
    console.log('🔄 RestaurantCache - Refrescando restaurantes...');
    
    // Limpiar cache y localStorage
    invalidateCache('access');
    invalidateCache('user');
    
    // Limpiar localStorage específico
    localStorage.removeItem(`${USER_RESTAURANTS_KEY}_${userId}`);
    localStorage.removeItem(`${USER_RESTAURANTS_TIMESTAMP_KEY}_${userId}`);
    
    // Cargar datos frescos desde la API
    if (userId) {
      try {
        // Cargar restaurantes con acceso
        await loadAccessRestaurants(userId, userEmail);
        
        // Cargar restaurantes del usuario
        await loadUserRestaurants(userId);
        
        console.log('✅ RestaurantCache - Restaurantes refrescados exitosamente');
      } catch (error) {
        console.error('❌ RestaurantCache - Error refrescando restaurantes:', error);
      }
    }
  }, [invalidateCache, loadAccessRestaurants, loadUserRestaurants]);

  // Limpiar todo el cache
  const clearCache = useCallback(() => {
    console.log('🧹 RestaurantCache - Limpiando todo el cache');
    setPublicRestaurants([]);
    setPublicRestaurantsLoaded(false);
    setUserRestaurants([]);
    setAccessRestaurants([]);
    userRestaurantsCache.current.clear();
    accessRestaurantsCache.current.clear();
    publicLoadingPromise.current = null;
    userLoadingPromises.current.clear();
    accessLoadingPromises.current.clear();
    checkRestaurantsPromises.current.clear();
    
    // Limpiar localStorage
    localStorage.removeItem(ACCESS_RESTAURANTS_KEY);
    localStorage.removeItem(ACCESS_RESTAURANTS_TIMESTAMP_KEY);
    
    // Limpiar localStorage de restaurantes del usuario (todos los usuarios)
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(USER_RESTAURANTS_KEY) || key.startsWith(USER_RESTAURANTS_TIMESTAMP_KEY)) {
        localStorage.removeItem(key);
      }
    });
  }, []);

  return (
    <RestaurantCacheContext.Provider value={{
      publicRestaurants,
      loadingPublicRestaurants,
      errorPublicRestaurants,
      loadPublicRestaurants,
      
      userRestaurants,
      loadingUserRestaurants,
      errorUserRestaurants,
      loadUserRestaurants,
      
      accessRestaurants,
      loadingAccessRestaurants,
      errorAccessRestaurants,
      loadAccessRestaurants,
      
      checkUserHasRestaurants,
      
      clearCache,
      invalidateCache,
      
      loadAccessRestaurantsFromStorage,
      saveAccessRestaurantsToStorage,
      loadUserRestaurantsFromStorage,
      saveUserRestaurantsToStorage,
      refreshRestaurants,
    }}>
      {children}
    </RestaurantCacheContext.Provider>
  );
};

export const useRestaurantCache = () => {
  const ctx = useContext(RestaurantCacheContext);
  if (!ctx) throw new Error('useRestaurantCache debe usarse dentro de RestaurantCacheProvider');
  return ctx;
}; 